import React, { useEffect, useMemo, useState, useRef } from "react";
import moment from "moment";
import { MetricSkeleton } from "./SkeletonLoaders";
import { getOnboardingConversionMetrics, getTenantListFilterOptions, getUserMetrics } from "../../context/functions/AdminFunction";
import { X } from "lucide-react";
import "../../styles/tenant-metrics.css";

const formatMedianTime = (seconds) => {
  const totalSeconds = Math.round(seconds || 0);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
};

const formatRate = (value) => {
  const numericValue = Number(value || 0);
  const percentValue = numericValue <= 1 ? numericValue * 100 : numericValue;
  return `${Number(percentValue.toFixed(1)).toString()}%`;
};

const TenantMetricsSection = () => {
  const defaults = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 30);

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    return { start: formatDate(start), end: formatDate(end) };
  }, []);

  const [dateRange, setDateRange] = useState({
    start_date: defaults.start,
    end_date: defaults.end,
  });
  const [tenantMetrics, setTenantMetrics] = useState({ loading: true, data: null });
  const [userMetrics, setUserMetrics] = useState({ loading: true, data: null });
  const [filterOptions, setFilterOptions] = useState({
    nationalities: [],
    employment_statuses: [],
    landlords: [],
  });
  const [selectedFilters, setSelectedFilters] = useState({
    employmentStatuses: [],
    nationalities: [],
    landlordIds: [],
  });
  const [openFilterMenu, setOpenFilterMenu] = useState(null);
  const [landlordSearchTerm, setLandlordSearchTerm] = useState("");
  const [showDateRangeMenu, setShowDateRangeMenu] = useState(false);
  const [isCustomDateMode, setIsCustomDateMode] = useState(false);
  const filterPanelRef = useRef(null);
  const metricsAbortControllerRef = useRef(null);
  const optionsAbortControllerRef = useRef(null);

  const fetchData = async (start = "", end = "") => {
    if (metricsAbortControllerRef.current) {
      metricsAbortControllerRef.current.abort();
    }

    const controller = new AbortController();
    metricsAbortControllerRef.current = controller;

    setTenantMetrics((prev) => ({ ...prev, loading: true }));
    setUserMetrics((prev) => ({ ...prev, loading: true }));
    try {
      const onboardingOptions = {};
      const userOptions = {};
      if (start) {
        onboardingOptions.start_date = moment(start).startOf("day").toISOString();
        userOptions.start_date = moment(start).startOf("day").toISOString();
      }
      if (end) {
        onboardingOptions.end_date = moment(end).endOf("day").toISOString();
        userOptions.end_date = moment(end).endOf("day").toISOString();
      }
      onboardingOptions.nationality = selectedFilters.nationalities;
      onboardingOptions.employment_status = selectedFilters.employmentStatuses;
      onboardingOptions.landlord_id = selectedFilters.landlordIds;
      userOptions.nationality = selectedFilters.nationalities;
      userOptions.employment_status = selectedFilters.employmentStatuses;
      userOptions.landlord_id = selectedFilters.landlordIds;
      onboardingOptions.signal = controller.signal;
      userOptions.signal = controller.signal;

      const [onboardingRes, userRes] = await Promise.all([
        getOnboardingConversionMetrics(onboardingOptions),
        getUserMetrics(userOptions),
      ]);

      setTenantMetrics({ loading: false, data: onboardingRes?.data || null });
      setUserMetrics({ loading: false, data: userRes?.data || null });
    } catch (error) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        return;
      }
      console.error("Failed to fetch tenant metrics", error);
      setTenantMetrics({ loading: false, data: null });
      setUserMetrics({ loading: false, data: null });
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    optionsAbortControllerRef.current = controller;

    const loadFilterOptions = async () => {
      const response = await getTenantListFilterOptions(controller.signal);
      if (response?.status === "canceled") return;
      if (response?.success && response?.data) {
        setFilterOptions({
          nationalities: response.data.nationalities || [],
          employment_statuses: response.data.employment_statuses || [],
          landlords: response.data.landlords || [],
        });
      }
    };

    loadFilterOptions();

    const handleClickOutside = (event) => {
      if (filterPanelRef.current && !filterPanelRef.current.contains(event.target)) {
        setOpenFilterMenu(null);
        setShowDateRangeMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      if (metricsAbortControllerRef.current) {
        metricsAbortControllerRef.current.abort();
      }
      if (optionsAbortControllerRef.current) {
        optionsAbortControllerRef.current.abort();
      }
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    fetchData(dateRange.start_date, dateRange.end_date);
  }, [selectedFilters.employmentStatuses, selectedFilters.nationalities, selectedFilters.landlordIds]);

  useEffect(() => {
    fetchData(dateRange.start_date, dateRange.end_date);
  }, [dateRange.start_date, dateRange.end_date]);

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange((prev) => ({ ...prev, [name]: value }));
  };

  const applyLast30Days = () => {
    setDateRange({ start_date: defaults.start, end_date: defaults.end });
    setIsCustomDateMode(false);
    setShowDateRangeMenu(false);
  };

  const applyAllTime = () => {
    setDateRange({ start_date: "", end_date: "" });
    setIsCustomDateMode(false);
    setShowDateRangeMenu(false);
  };

  const enableCustomDateMode = () => {
    setIsCustomDateMode(true);
    setShowDateRangeMenu(false);
  };

  const getDateRangeLabel = () => {
    if (dateRange.start_date === defaults.start && dateRange.end_date === defaults.end) {
      return "Last 30 Days";
    }
    if (!dateRange.start_date && !dateRange.end_date) return "All Time";
    if (isCustomDateMode) return "Custom";
    return "Custom";
  };

  const toggleMultiValue = (key, value) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [key]: prev[key].includes(value)
        ? prev[key].filter((item) => item !== value)
        : [...prev[key], value],
    }));
  };

  const clearFilter = (key) => {
    setSelectedFilters((prev) => ({ ...prev, [key]: [] }));
  };

  const getFilterSummary = (values, label) => {
    if (!values.length) return `All ${label}`;
    if (values.length === 1) {
      if (label === "statuses") return getStatusLabel(values[0]);
      return values[0];
    }
    return `${values.length} selected`;
  };

  const getStatusLabel = (status) => {
    const clean = (status || "").split("_").join(" ");
    return clean ? clean[0].toUpperCase() + clean.slice(1) : "";
  };

  const filteredLandlords = filterOptions.landlords.filter((landlord) => {
    if (!landlordSearchTerm.trim()) return true;
    const term = landlordSearchTerm.toLowerCase();
    return (
      (landlord.name || "").toLowerCase().includes(term) ||
      (landlord.email || "").toLowerCase().includes(term)
    );
  });

  return (
    <div className="mt-5 mb-5">
      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center mb-3 gap-3">
        <h4 style={{ fontWeight: 600, color: "#2c3e50", margin: 0 }}>Tenant Metrics</h4>
      </div>
      <div className="tenant-filter-panel mb-4" ref={filterPanelRef}>
        <div className="tenant-filter-row">
          <div className="tenant-filter-group">
            <button
              type="button"
              className={`range-chip ${showDateRangeMenu ? "range-chip-filter-active" : ""}`}
              onClick={() => setShowDateRangeMenu((prev) => !prev)}
            >
              {getDateRangeLabel()}
            </button>
            {showDateRangeMenu && (
              <div className="range-menu">
                <button type="button" className="range-menu-item" onClick={applyLast30Days}>
                  Last 30 Days
                </button>
                <button type="button" className="range-menu-item" onClick={applyAllTime}>
                  All Time
                </button>
                <button type="button" className="range-menu-item" onClick={enableCustomDateMode}>
                  Custom
                </button>
              </div>
            )}
          </div>
          <div className="tenant-filter-group">
            <button
              type="button"
              className={`range-chip range-chip-filter ${openFilterMenu === "employmentStatuses" ? "range-chip-filter-active" : ""}`}
              onClick={() => setOpenFilterMenu((prev) => (prev === "employmentStatuses" ? null : "employmentStatuses"))}
            >
              <span>{getFilterSummary(selectedFilters.employmentStatuses, "statuses")}</span>
            </button>
            {openFilterMenu === "employmentStatuses" && (
              <div className="tenant-filter-dropdown">
                <div className="tenant-filter-dropdown-list">
                  {filterOptions.employment_statuses.filter(Boolean).map((status) => (
                    <label key={status} className="tenant-filter-option">
                      <input
                        type="checkbox"
                        checked={selectedFilters.employmentStatuses.includes(status)}
                        onChange={() => toggleMultiValue("employmentStatuses", status)}
                      />
                      <span>{getStatusLabel(status)}</span>
                    </label>
                  ))}
                </div>
                <div className="tenant-filter-actions">
                  <button type="button" onClick={() => clearFilter("employmentStatuses")}>Clear</button>
                </div>
              </div>
            )}
          </div>
          <div className="tenant-filter-group">
            <button
              type="button"
              className={`range-chip range-chip-filter ${openFilterMenu === "nationalities" ? "range-chip-filter-active" : ""}`}
              onClick={() => setOpenFilterMenu((prev) => (prev === "nationalities" ? null : "nationalities"))}
            >
              <span>{getFilterSummary(selectedFilters.nationalities, "nationalities")}</span>
            </button>
            {openFilterMenu === "nationalities" && (
              <div className="tenant-filter-dropdown">
                <div className="tenant-filter-dropdown-list">
                  {filterOptions.nationalities.map((country) => (
                    <label key={country} className="tenant-filter-option">
                      <input
                        type="checkbox"
                        checked={selectedFilters.nationalities.includes(country)}
                        onChange={() => toggleMultiValue("nationalities", country)}
                      />
                      <span>{country}</span>
                    </label>
                  ))}
                </div>
                <div className="tenant-filter-actions">
                  <button type="button" onClick={() => clearFilter("nationalities")}>Clear</button>
                </div>
              </div>
            )}
          </div>
          <div className="tenant-filter-group">
            <button
              type="button"
              className={`range-chip range-chip-filter ${openFilterMenu === "landlordIds" ? "range-chip-filter-active" : ""}`}
              onClick={() =>
                setOpenFilterMenu((prev) => {
                  const next = prev === "landlordIds" ? null : "landlordIds";
                  if (next === "landlordIds") setLandlordSearchTerm("");
                  return next;
                })
              }
            >
              <span>{selectedFilters.landlordIds.length ? `${selectedFilters.landlordIds.length} selected` : "All landlords"}</span>
            </button>
            {openFilterMenu === "landlordIds" && (
              <div className="tenant-filter-dropdown">
                <div className="tenant-filter-search-wrap">
                  <input
                    type="text"
                    className="tenant-filter-search-input"
                    placeholder="Search landlord name or email..."
                    value={landlordSearchTerm}
                    onChange={(e) => setLandlordSearchTerm(e.target.value)}
                  />
                </div>
                <div className="tenant-filter-dropdown-list">
                  {filteredLandlords.map((landlord) => (
                    <label key={landlord.id} className="tenant-filter-option">
                      <input
                        type="checkbox"
                        checked={selectedFilters.landlordIds.includes(landlord.id)}
                        onChange={() => toggleMultiValue("landlordIds", landlord.id)}
                      />
                      <span className="tenant-filter-option-content">
                        <span className="tenant-filter-option-title">{landlord.name}</span>
                        {landlord.email && <span className="tenant-filter-option-subtitle">{landlord.email}</span>}
                      </span>
                    </label>
                  ))}
                  {!filteredLandlords.length && <div className="tenant-filter-empty">No landlords found</div>}
                </div>
                <div className="tenant-filter-actions">
                  <button type="button" onClick={() => clearFilter("landlordIds")}>Clear</button>
                </div>
              </div>
            )}
          </div>
        </div>
        {isCustomDateMode && (
          <div className="d-flex align-items-center gap-2 flex-wrap mt-2">
            <input
              type="date"
              name="start_date"
              value={dateRange.start_date}
              onChange={handleDateChange}
              className="date-input compact-date-input"
            />
            <input
              type="date"
              name="end_date"
              value={dateRange.end_date}
              onChange={handleDateChange}
              className="date-input compact-date-input"
            />
          </div>
        )}
        <div className="tenant-filter-chip-row">
          {selectedFilters.employmentStatuses.map((item) => (
            <button
              key={`employment-${item}`}
              type="button"
              className="tenant-filter-chip"
              onClick={() => toggleMultiValue("employmentStatuses", item)}
            >
              {getStatusLabel(item)} <X size={12} />
            </button>
          ))}
          {selectedFilters.nationalities.map((item) => (
            <button
              key={`nationality-${item}`}
              type="button"
              className="tenant-filter-chip"
              onClick={() => toggleMultiValue("nationalities", item)}
            >
              {item} <X size={12} />
            </button>
          ))}
          {selectedFilters.landlordIds.map((item) => {
            const landlord = filterOptions.landlords.find((ll) => ll.id === item);
            const name = landlord?.name || item;
            return (
              <button
                key={`landlord-${item}`}
                type="button"
                className="tenant-filter-chip"
                onClick={() => toggleMultiValue("landlordIds", item)}
              >
                {name} <X size={12} />
              </button>
            );
          })}
        </div>
      </div>

      {tenantMetrics.loading || userMetrics.loading ? (
        <MetricSkeleton count={4} />
      ) : tenantMetrics.data ? (
        <>
          <OnboardingFunnel onboarding={tenantMetrics.data} userMetrics={userMetrics.data} />

          {userMetrics.data?.metrics && (
            <div className="user-metrics-section mt-4">
              <div className="user-metrics-header">
                <h2>User Metrics - Completed Profiles Only</h2>
              </div>
              <div className="user-metrics-grid">
                <div className="user-metric-tile">
                  <div className="user-metric-title">Avg docs uploaded per profile</div>
                  <div className="user-metric-value">{Math.round(Number(userMetrics.data.metrics.avg_docs_per_profile || 0))}</div>
                  <div className="user-metric-subtitle">completed profiles only</div>
                </div>
                <div className="user-metric-tile">
                  <div className="user-metric-title">Cl@ve usage rate</div>
                  <div className="user-metric-value">{formatRate(userMetrics.data.metrics.clave_usage_rate)}</div>
                  <div className="user-metric-subtitle">of completed profiles</div>
                </div>
                <div className="user-metric-tile">
                  <div className="user-metric-title">Bank connected</div>
                  <div className="user-metric-value">{formatRate(userMetrics.data.metrics.bank_connected_rate)}</div>
                  <div className="user-metric-subtitle">of completed profiles</div>
                </div>
                <div className="user-metric-tile">
                  <div className="user-metric-title">Tenants with guarantor</div>
                  <div className="user-metric-value">{formatRate(userMetrics.data.metrics.tenants_with_guarantor_rate)}</div>
                  <div className="user-metric-subtitle">of completed profiles</div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-5" style={{ backgroundColor: "#f8f9fa", borderRadius: "12px", border: "1px dashed #dee2e6" }}>
          <p className="text-muted m-0">No tenant metric data available for the selected period.</p>
        </div>
      )}
    </div>
  );
};

function OnboardingFunnel({ onboarding, userMetrics }) {
  const funnel = onboarding?.funnel || {};
  const totalLeads = Number(funnel.reached_step_1?.count || 0);
  const safeLeads = totalLeads || 1;

  const funnelSteps = [
    { key: "leads", label: "Leads", count: totalLeads, percentage: funnel.reached_step_1?.percentage || 0, median_time_seconds: Number(funnel.reached_step_1?.median_time_seconds || 0) },
    { key: "basic", label: "Basic Info", count: Number(funnel.passed_step_1?.count || 0), percentage: funnel.passed_step_1?.percentage || 0, median_time_seconds: Number(funnel.passed_step_1?.median_time_seconds || 0) },
    { key: "docs", label: "Docs", count: Number(funnel.passed_step_2?.count || 0), percentage: funnel.passed_step_2?.percentage || 0, median_time_seconds: Number(funnel.passed_step_2?.median_time_seconds || 0) },
    { key: "kyc", label: "KYC", count: Number(funnel.passed_step_3?.count || 0), percentage: funnel.passed_step_3?.percentage || 0, median_time_seconds: Number(funnel.passed_step_3?.median_time_seconds || 0) },
    { key: "profile", label: "Profile Visible", count: Number(funnel.completed_profile?.count || 0), percentage: funnel.completed_profile?.percentage || 0, median_time_seconds: Number(funnel.completed_profile?.median_time_seconds || 0) },
  ];

  const withStats = funnelSteps.map((step, index) => {
    const prev = index > 0 ? funnelSteps[index - 1].count : step.count;
    const drop = index === 0 || prev <= 0 ? 0 : ((step.count - prev) / prev) * 100;
    return { ...step, conversion: step.percentage, drop };
  });

  const endToEndRate = funnel.completed_profile?.percentage || 0;
  const profileVisibleCount = Number(funnel.completed_profile?.count || 0);

  const getConversionColor = (value) => {
    if (value >= 80) return "#3f8ddf";
    if (value >= 60) return "#2fa66f";
    return "#e6a33a";
  };

  return (
    <div className="onboarding-funnel-section">
      <div className="onboarding-funnel-header">
        <h2>Onboarding Funnel</h2>
      </div>
      <div className="onboarding-funnel-table">
        {withStats.map((step) => {
          const conversionColor = getConversionColor(step.conversion);
          return (
            <div className="funnel-step-column" key={step.key}>
              <div className="funnel-step-column-header">{step.label}</div>
              <div className="funnel-step-column-body">
                <div className="funnel-step-value">{step.count}</div>
                <div className="funnel-step-conversion" style={{ color: conversionColor }}>{Math.round(step.conversion)}%</div>
              </div>
              <div className="funnel-step-bar">
                <div
                  className="funnel-step-bar-fill"
                  style={{ width: `${Math.max(0, Math.min(100, step.conversion))}%`, backgroundColor: conversionColor }}
                />
              </div>
              <div className="funnel-step-drop" style={{ color: "#8f7748" }}>
                {step.key === "leads" ? "baseline" : `${step.drop < 0 ? "-" : "+"}${Math.round(Math.abs(step.drop))}% drop`}
              </div>
              <div className="funnel-step-median">{step.key === "leads" ? "-" : `median - ${formatMedianTime(step?.median_time_seconds)}`}</div>
            </div>
          );
        })}
      </div>

      <div className="onboarding-funnel-notes">
        <p className="funnel-note">
          <span className="funnel-note-dot funnel-note-dot-blue" />
          <strong>End-to-end completion:</strong> {profileVisibleCount} of {totalLeads} leads reached a visible profile - <strong>{endToEndRate.toFixed(1)}%</strong>.
        </p>
        <p className="funnel-note">
          <span className="funnel-note-dot funnel-note-dot-amber" />
          <strong>Median time</strong> is cumulative from lead capture.
        </p>
      </div>
    </div>
  );
}

export default TenantMetricsSection;
